import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import '@/shared/tracking/events.quiz-share-guest';
import '@/shared/tracking/events.quiz-trial-gate-copy';

import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';

import { useOpenWindow } from '@/hooks';
import { useUser } from '@/lib/auth';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { copyQuizToLocation } from '@/shared/features/quiz-folder/api';
import {
  buildCopyPath,
  type CopyHandoffMessage,
  isCopyHandoffMessage,
} from '@/shared/quizCopy/utils/copyHandoff';
import { type QuizCopySource } from '@/shared/tracking/quizCopySource';
import { alertDialog } from '@/ui_v3';

import { QUIZ_COPY_TEXT } from '../consts/text';
import { showQuizCopiedDialog } from '../showQuizCopiedDialog';
import { trackQuizCopyComplete } from '../utils/trackCopyComplete';

/**
 * 진행 중인 복사 요청. 복사 버튼이 여러 진입점에 있어도 같은 원본이 두 번 복사되지 않게
 * 훅 인스턴스를 가로질러 **동기적으로** 잠근다 — state 는 다음 렌더까지 연타를 못 막는다.
 */
const copyingQuizIds = new Set<string>();

/**
 * 인증을 맡긴 탭 → 그 탭의 결과를 보여 줄 함수.
 *
 * **훅 인스턴스가 아니라 모듈에 둔다.** 공유 모달처럼 `복사하기` 를 누르자마자 닫히는 진입점이
 * 있다 — 수신을 훅에 묶으면 모달과 함께 리스너가 사라져, 인증을 마치고 돌아온 결과를 아무도
 * 받지 못하고 원탭에는 아무것도 뜨지 않는다.
 */
const pendingHandoffs = new Map<
  Window,
  (message: CopyHandoffMessage) => void
>();
let isHandoffListenerAttached = false;

/** 결과 수신 리스너를 한 번만 붙인다. 페이지가 살아 있는 동안 떼지 않는다. */
const ensureHandoffListener = () => {
  if (isHandoffListenerAttached) return;
  isHandoffListenerAttached = true;

  window.addEventListener('message', (event: MessageEvent<unknown>) => {
    // 같은 오리진 + 내가 연 창. 확장 프로그램·다른 iframe 메시지를 거른다.
    if (event.origin !== window.location.origin) return;
    const present = pendingHandoffs.get(event.source as Window);
    if (!present || !isCopyHandoffMessage(event.data)) return;

    pendingHandoffs.delete(event.source as Window);
    present(event.data);
  });
};

/** 아직 열려 있는 인증 탭. 로그인 없이 닫힌 탭은 결과가 오지 않으므로 이때 걷어 낸다. */
const findOpenAuthTab = () => {
  for (const tab of pendingHandoffs.keys()) {
    if (!tab.closed) return tab;
    pendingHandoffs.delete(tab);
  }
  return null;
};

export interface CopyQuizOptions {
  /**
   * 복사 경로까지 따라갈 유입 출처. 넘기면 URL 로 실려 인증 왕복을 넘어 살아남는다.
   *
   * `location`(= referrer)과 겹쳐 보이지만 수명이 다르다 — referrer 는 TTL 5분이라
   * 회원가입 6화면을 넘기면 사라진다. 전환 퍼널을 이으려면 쿼리여야 한다.
   */
  copySource?: QuizCopySource;
  /** 공유 클릭 한 번을 끝까지 잇는 시도 ID. 인증 왕복을 넘어 저장까지 따라간다. */
  shareFlowId?: string;
  /** 체험 제한 화면 복사를 체험 세션에 잇는 ID. */
  trialSessionId?: string;
}

/**
 * 플레이 주변(공유 모달·체험 제한·튜토리얼·젤리)의 `복사하기`.
 *
 * **하던 것을 끊지 않는 것**이 이 훅의 목적이다. 예전에는 로그인 여부와 무관하게 새 탭을 열어
 * `/duplicate/{id}` 빌드 페이지로 보냈고, 사용자의 시선과 작업이 그 탭으로 넘어갔다.
 *
 *  - **로그인 상태** → 탭을 열지 않고 제자리에서 복사하고 토스트만 띄운다
 *  - **게스트** → 새 탭에 인증만 맡긴다. 그 탭이 복사까지 끝내고 결과를 돌려준 뒤 이 탭으로 돌아오라고 안내한다
 *    (`/copy/[quizsetId]`). 원탭은 **결과 표시만** 하므로 보류 상태를 갖지 않는다.
 *    결과는 사용자가 언제 돌아올지 모르므로 사라지는 토스트가 아니라 닫을 때까지 남는 모달로 보인다
 *
 * 게스트 쪽에서 복사를 원탭이 하지 않는 이유는 `copyHandoff.ts` 에 적어 두었다 — 원탭의
 * `useUser` 는 인증 전이라 계정 유형을 몰라 학생 차단 판정을 할 수 없다.
 */
export const useCopyQuiz = ({
  onResultDismissed,
}: {
  /**
   * 결과 안내를 닫은 뒤 한 번. 플레이에서는 `useRequestPlayIframeFocus()` 를 넘겨 게임이
   * 키보드 포커스를 되찾게 한다. 이 훅은 play 모듈을 알지 않으므로 호출부가 넘긴다.
   */
  onResultDismissed?: () => void;
} = {}) => {
  const router = useRouter();
  /* `common` 은 모든 페이지가 로드한다 — 진입점마다 네임스페이스를 챙기지 않아도 된다. */
  const { t } = useTranslation(['common']);
  const { user } = useUser();
  const openWindow = useOpenWindow();
  const [isCopying, setIsCopying] = useState(false);

  const presentResult = useCallback(
    (message: CopyHandoffMessage) => {
      void (async () => {
        try {
          /*
            토스트로 접지 않는다. 사용자는 인증 탭에서 언제 돌아올지 모르고, 돌아왔을 때 이미
            사라진 알림은 "아무 일도 안 일어났다"와 구분되지 않는다.
          */
          if (message.result === 'failed') {
            await alertDialog.openAsync({
              header: { title: t('common:quizDetail.copy.failed') },
            });
            return;
          }
          // 착지 탭의 차단 화면과 같은 말을 한다.
          if (message.result === 'blocked') {
            await alertDialog.openAsync({
              header: { title: QUIZ_COPY_TEXT.blockedTitle },
              body: QUIZ_COPY_TEXT.blockedDescription,
            });
            return;
          }
          const choice = await showQuizCopiedDialog();
          if (choice === 'view') {
            await router.push(`/quiz/${message.copiedQuizsetId}`);
            return;
          }
        } finally {
          /*
            탭을 오갔으므로 게임이 키보드 포커스를 잃은 상태다. 돌려주지 않으면 모달을 닫아도
            조용히 먹통이 된다 — `JellyMenuFlow` 가 이미 같은 실패를 기록해 두었다.
            플레이 밖(자료실 등)에서 부르면 넘기지 않으므로 아무 일도 일어나지 않는다.
          */
          onResultDismissed?.();
        }
      })();
    },
    [router, t, onResultDismissed],
  );

  const copyQuiz = useCallback(
    (quizId: string, location: string, options?: CopyQuizOptions) => {
      setReferrer({ from: '/play', location });

      const copyContext = {
        copySource: options?.copySource,
        shareFlowId: options?.shareFlowId,
        trialSessionId: options?.trialSessionId,
      };

      if (user) {
        if (copyingQuizIds.has(quizId)) return;
        copyingQuizIds.add(quizId);
        setIsCopying(true);
        void (async () => {
          try {
            const copied = await copyQuizToLocation({
              sourceQuizsetId: quizId,
            });
            trackQuizCopyComplete(copyContext, quizId, {
              status: 'success',
              copiedQuizsetId: copied.id,
            });
            zusToast.success(QUIZ_COPY_TEXT.copied);
          } catch (error) {
            trackQuizCopyComplete(copyContext, quizId, {
              status: 'fail',
              error,
            });
            zusToast.error(t('common:quizDetail.copy.failed'));
          } finally {
            copyingQuizIds.delete(quizId);
            setIsCopying(false);
          }
        })();
        return;
      }

      /*
        게스트는 잠그지 않는다 — 인증 탭을 로그인 없이 닫으면 결과 메시지가 오지 않아 풀 시점이
        없다. 대신 아직 열려 있는 인증 탭이 있으면 새로 열지 않고 그 탭을 앞으로 가져온다.
      */
      const openAuthTab = findOpenAuthTab();
      if (openAuthTab) {
        openAuthTab.focus();
        return;
      }

      // 로그인 사용자는 이 칸을 건너뛴다. copy_click 대비 비율이 곧 비로그인 비중이다.
      if (options?.copySource === 'guest_share' && options.shareFlowId) {
        trackEvent('quiz_share_guest_auth_start', {
          share_flow_id: options.shareFlowId,
          quiz_set_id: quizId,
        });
      }
      if (options?.copySource === 'trial_gate' && options.trialSessionId) {
        trackEvent('quiz_play_trial_gate_auth_start', {
          trial_session_id: options.trialSessionId,
          quiz_set_id: quizId,
        });
      }

      const copyPath = buildCopyPath(quizId, copyContext);
      /*
        클릭에서 **동기적으로** 열어야 브라우저가 막지 않는다. `await` 뒤로 미루면 차단된다.
        막히면 `null` 이라, 현재 탭을 보내 착지 경로가 opener 없이 처리하게 한다.
      */
      const authTab = openWindow(
        `/login?callbackUrl=${encodeURIComponent(copyPath)}`,
      );
      if (authTab) {
        // 이 컴포넌트가 곧 사라져도(공유 모달) 결과는 모듈 리스너가 받아 이 함수로 보여 준다.
        pendingHandoffs.set(authTab, presentResult);
        ensureHandoffListener();
        return;
      }
      void router.push(`/login?callbackUrl=${encodeURIComponent(copyPath)}`);
    },
    [user, t, openWindow, router, presentResult],
  );

  return { copyQuiz, isCopying };
};
